import { useMutation, useQueryClient } from "@tanstack/react-query";
// Cross-feature dependency (deliberate): cancelling a job can delete/modify employees
// server-side, so jobs refreshes the employees cache. Kept as a direct import rather than a
// shared key — the coupling is one small, documented contract, not a general boundary breach.
import { EMPLOYEES_QUERY_KEYS } from "@/features/employees/configs/queryKeys";
import { JOB_MESSAGES } from "../configs/constants";
import { JOBS_QUERY_KEYS } from "../configs/queryKeys";
import {
  postJob, postJobToCreateVoucher, cancelJob, cancelEmployeeImport,
} from "../services/jobs.service";
import { useJobContext } from "./useJobContext";
import type {
  CreateJobParams,
  CreateJobToCreateVoucherParams,
  Job,
  GetJobsServiceResponse,
} from "../types/jobs.types";

// ===== TYPES =====
type CachePageData = GetJobsServiceResponse;

type CacheData = {
  pages: CachePageData[]
  pageParams: unknown[]
};

type MutationContext = { previousData?: CacheData };

// ===== UTILITIES =====
/**
 * Common cache update utilities
 */
const cacheUtils = {
  /**
   * Cancel ongoing queries and get current cache data
   */
  async prepareCache(queryClient: ReturnType<typeof useQueryClient>) {
    await queryClient.cancelQueries({ queryKey: JOBS_QUERY_KEYS.JOBS });
    const previousData = queryClient.getQueryData<CacheData>(JOBS_QUERY_KEYS.JOBS_INFINITE);
    return { previousData };
  },

  /**
   * Rollback cache to previous state on error
   */
  rollbackCache(queryClient: ReturnType<typeof useQueryClient>, context?: MutationContext) {
    if (context?.previousData) {
      queryClient.setQueryData(JOBS_QUERY_KEYS.JOBS_INFINITE, context.previousData);
    }
  },

  /**
   * Add job to cache (optimistic create)
   */
  addToCache(queryClient: ReturnType<typeof useQueryClient>, job: Job) {
    // Update the cache with default params (most common case)
    const defaultParams = {
      limit: 20,
      sort: true,
      search: "",
    };

    queryClient.setQueryData<CacheData>(
      [...JOBS_QUERY_KEYS.JOBS_INFINITE, defaultParams],
      (old) => {
        if (!old) return old;

        const newPages = [...old.pages];
        if (newPages[0]) {
          newPages[0] = {
            ...newPages[0],
            jobs: [job, ...newPages[0].jobs],
            metadata: {
              ...newPages[0].metadata,
              total_items: (newPages[0].metadata?.total_items || 0) + 1,
            },
          };
        }

        return { ...old, pages: newPages };
      },
    );

    // Also invalidate all jobs queries to ensure they refetch if needed
    queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.JOBS_INFINITE });
  },

  /**
   * Remove job from cache (optimistic delete/cancel)
   */
  removeFromCache(queryClient: ReturnType<typeof useQueryClient>, jobId: string) {
    queryClient.setQueryData<CacheData>(JOBS_QUERY_KEYS.JOBS_INFINITE, (old) => {
      if (!old) return old;

      const newPages = old.pages.map((page) => ({
        ...page,
        jobs: page.jobs.filter((job) => job.uuid !== jobId),
        metadata: {
          ...page.metadata,
          total_items: Math.max((page.metadata?.total_items || 1) - 1, 0),
        },
      }));

      return { ...old, pages: newPages };
    });
  },
};

// Refresh the employee lists after a cancellation. Scoped to the paginated list keys
// (never EMPLOYEES_QUERY_KEYS.all) so open employee detail pages are not needlessly refetched.
const invalidateEmployeeLists = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: [...EMPLOYEES_QUERY_KEYS.all, "infinite"] });

// ===== MUTATIONS =====

/**
 * Create job mutation with optimistic updates
 */
export const useCreateJob = () => {
  const queryClient = useQueryClient();
  const { addNewJobToPushedJobs } = useJobContext();

  return useMutation({
    mutationFn: (payload: CreateJobParams) => postJob(payload),
    meta: {
      successMessage: JOB_MESSAGES.CREATE_SUCCESS,
      errorMessage: JOB_MESSAGES.CREATE_ERROR,
      invalidateQueries: [JOBS_QUERY_KEYS.JOBS],
    },
    onSuccess: (response) => {
      // Add the created job to cache
      if (response.data?.job) {
        cacheUtils.addToCache(queryClient, response.data.job);
        // Also add to pushedJobs for instant badge update
        addNewJobToPushedJobs(response.data.job);
      }
    },
    onError: (_err, _payload, context) => {
      cacheUtils.rollbackCache(queryClient, context as MutationContext);
    },
  });
};

/**
 * Create voucher job mutation with optimistic updates
 */
export const useCreateVoucherJob = () => {
  const queryClient = useQueryClient();
  const { addNewJobToPushedJobs } = useJobContext();

  return useMutation({
    mutationFn: (payload: CreateJobToCreateVoucherParams) => postJobToCreateVoucher(payload),
    meta: {
      successMessage: JOB_MESSAGES.VOUCHER_CREATE_SUCCESS,
      errorMessage: JOB_MESSAGES.VOUCHER_CREATE_ERROR,
      invalidateQueries: [JOBS_QUERY_KEYS.JOBS],
    },
    onSuccess: (response) => {
      // Add the created job to cache
      if (response.data?.job) {
        cacheUtils.addToCache(queryClient, response.data.job);
        // Also add to pushedJobs for instant badge update
        addNewJobToPushedJobs(response.data.job);
      }
    },
    onError: (_err, _payload, context) => {
      cacheUtils.rollbackCache(queryClient, context as MutationContext);
    },
  });
};

/**
 * Cancel job mutation with optimistic updates
 */
export const useCancelJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => cancelJob(jobId),
    meta: {
      successMessage: JOB_MESSAGES.CANCEL_SUCCESS,
      errorMessage: JOB_MESSAGES.CANCEL_ERROR,
      // Scope to the list only: invalidating the whole ["jobs"] tree would also refetch
      // the open job's detail query (["jobs", "single", id]), which 404s on the job we
      // just deleted. See JOB_SINGLE in queryKeys.
      invalidateQueries: [JOBS_QUERY_KEYS.JOBS_INFINITE],
    },
    onMutate: async (jobId) => {
      const context = await cacheUtils.prepareCache(queryClient);
      cacheUtils.removeFromCache(queryClient, jobId);
      return context;
    },
    onSuccess: () => {
      // Certains jobs génériques suppriment/modifient des salariés — on rafraîchit leurs listes.
      invalidateEmployeeLists(queryClient);
    },
    onError: (_err, _jobId, context) => {
      cacheUtils.rollbackCache(queryClient, context);
    },
  });
};

/**
 * Cancel an employee import asynchronously (reverse-progress).
 * Unlike useCancelJob, the job is NOT removed from the list and the modal stays open: the backend
 * flags it cancelling (returned by the 202) and drives the gauge down to 0 via Pusher before the
 * terminal "cancelled" status. We only refetch the list (never the whole ["jobs"] tree, which would
 * 404 the open job's detail query) so the row reflects the cancelling state.
 */
export const useCancelEmployeeImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => cancelEmployeeImport(jobId),
    meta: {
      successMessage: JOB_MESSAGES.CANCEL_IMPORT_PENDING,
      errorMessage: JOB_MESSAGES.CANCEL_ERROR,
      invalidateQueries: [JOBS_QUERY_KEYS.JOBS_INFINITE],
    },
    onSuccess: (_response, jobId) => {
      // The import job survives (not deleted): refresh its detail so the open modal reflects
      // the cancelling status without relying solely on the Pusher push.
      queryClient.invalidateQueries({ queryKey: JOBS_QUERY_KEYS.JOB_SINGLE(jobId) });
      // Employees are being deleted in the background — the list may have changed.
      invalidateEmployeeLists(queryClient);
    },
  });
};
